import { useMemo } from "react";

import { useGetComplianceInsights } from "../../../api/compliance";
import { ResponsesPieChart } from "../charts";
import SectionShell, { Stat, formatNum, formatPct } from "../SectionShell";

/**
 * The six buckets are mutually exclusive by construction — each ticket holder
 * is either their single response row, the absence of one, or a deleted
 * account — so they add up against the event's ticket count and the numbers
 * hold together when the organizer presents them.
 *
 * Opting out is its own bucket rather than folded into either neighbour: these
 * people are no longer covered by the terms, exactly like the decliners, but
 * they did accept once and that acceptance stood for a while. An organizer
 * answering "who agreed to this, and when did they stop?" needs both facts, and
 * a bucket that merged them would answer only half the question.
 */
function Compliance({ eventId }) {
  const { data, isPending, isError, error } = useGetComplianceInsights({
    eventId,
  });

  const breakdown = useMemo(
    () => [
      { name: "Accepted", value: data?.accepted ?? 0 },
      { name: "Declined", value: data?.declined ?? 0 },
      { name: "Accepted, then opted out", value: data?.optedOut ?? 0 },
      { name: "Saw the terms, did not answer", value: data?.seen ?? 0 },
      {
        name: "Never opened the event in the app",
        value: data?.neverOpened ?? 0,
      },
      { name: "Account deleted", value: data?.deletedAccount ?? 0 },
    ],
    [data]
  );

  return (
    <SectionShell
      id="compliance"
      title="Compliance"
      description="How the event's own terms landed with the audience, measured against the event's tickets."
      isPending={isPending}
      isError={isError}
      error={error}
      eventId={eventId}
      analysisKey="compliance"
      stats={
        <>
          <Stat
            part="accepted"
            label="Accepted"
            value={formatPct(data?.acceptanceRate ?? undefined)}
            hint={`${formatNum(data?.accepted)} of ${formatNum(
              data?.totalTickets
            )} ticket holders`}
          />
          <Stat
            part="declined"
            label="Declined"
            value={formatNum(data?.declined)}
          />
          <Stat
            part="optedOut"
            label="Accepted, then opted out"
            value={formatNum(data?.optedOut)}
          />
          <Stat
            part="seen"
            label="Saw the terms, did not answer"
            value={formatNum(data?.seen)}
          />
          <Stat
            part="neverOpened"
            label="Never opened the event in the app"
            value={formatNum(data?.neverOpened)}
          />
          <Stat
            part="deletedAccount"
            label="Account deleted"
            value={formatNum(data?.deletedAccount)}
          />
        </>
      }
      lead={
        <ResponsesPieChart
          part="complianceAnswers"
          title="Compliance answers"
          data={breakdown}
        />
      }
    />
  );
}

export default Compliance;
